const SECOND = 1000;
const PARTS = [
	{
		key: 'days',
		singularAttr: 'data-day-label',
		pluralAttr: 'data-days-label',
		fallbackSingular: 'day',
		fallbackPlural: 'days',
	},
	{
		key: 'hours',
		singularAttr: 'data-hour-label',
		pluralAttr: 'data-hours-label',
		fallbackSingular: 'hour',
		fallbackPlural: 'hours',
	},
	{
		key: 'minutes',
		singularAttr: 'data-minute-label',
		pluralAttr: 'data-minutes-label',
		fallbackSingular: 'minute',
		fallbackPlural: 'minutes',
	},
	{
		key: 'seconds',
		singularAttr: 'data-second-label',
		pluralAttr: 'data-seconds-label',
		fallbackSingular: 'second',
		fallbackPlural: 'seconds',
	},
];

class TextCountdownComponent extends HTMLElement {
	static get observedAttributes() {
		return ['data-date', 'data-time', 'data-timezone', 'data-daily'];
	}

	#timer = null;
	#target = null;
	#els = null;
	#daysMax = 1;

	connectedCallback() {
		this.setAttribute('role', 'timer');
		this.setAttribute('aria-live', 'polite');
		this.#ensureMarkup();
		this.#start();
	}

	disconnectedCallback() {
		this.#stop();
	}

	attributeChangedCallback() {
		if (this.isConnected && this.#els) this.#start();
	}

	get #daily() {
		return this.hasAttribute('data-daily');
	}

	#ensureMarkup() {
		if (this.#els) return;
		this.innerHTML = `
      ${PARTS.map((part, index) => `
      <span data-part="${part.key}">
        <span data-role="value">00</span>
        <span data-role="label">${this.#getLabel(part.key, 'plural')}</span>
      </span>
      ${index < PARTS.length - 1 ? '<span data-delimiter> </span>' : ''}
      `).join('')}
    `;

		this.#els = PARTS.reduce((acc, part) => {
			acc[part.key] = this.querySelector(`[data-part="${part.key}"] [data-role="value"]`);
			return acc;
		}, {});
	}

	#start() {
		this.#stop();
		this.removeAttribute('data-complete');
		this.#target = this.#parseTarget();

		if (!this.#target) {
			this.textContent = this.getAttribute('data-invalid-text') ?? '';
			return;
		}

		this.#daysMax = Math.max(Math.ceil((this.#target.getTime() - Date.now()) / 86400000), 1);
		this.#tick();
	}

	#stop() {
		if (this.#timer) clearTimeout(this.#timer);
		this.#timer = null;
	}

	#tick() {
		if (this.#daily && this.#target.getTime() <= Date.now()) {
			this.#target = this.#parseTarget();
		}

		const remaining = Math.max(this.#target.getTime() - Date.now(), 0);
		this.#render(remaining);

		if (remaining === 0 && !this.#daily) {
			this.setAttribute('data-complete', '');
			return;
		}

		const delay = SECOND - (Date.now() % SECOND);
		this.#timer = setTimeout(() => this.#tick(), delay);
	}

	#render(ms) {
		const totalSeconds = Math.floor(ms / SECOND);
		const days = Math.floor(totalSeconds / 86400);
		const hours = Math.floor((totalSeconds % 86400) / 3600);
		const minutes = Math.floor((totalSeconds % 3600) / 60);
		const seconds = totalSeconds % 60;

		this.#els.days.textContent = this.#pad(days);
		this.#els.hours.textContent = this.#pad(hours);
		this.#els.minutes.textContent = this.#pad(minutes);
		this.#els.seconds.textContent = this.#pad(seconds);

		const exactSeconds = ms / SECOND;
		const progress = {
			days: exactSeconds / 86400 / this.#daysMax,
			hours: (exactSeconds % 86400) / 86400,
			minutes: (exactSeconds % 3600) / 3600,
			seconds: (exactSeconds % 60) / 60,
		};
		for (const key in progress) {
			this.#els[key].parentElement.style.setProperty('--countdown-progress', progress[key]);
		}

		const ariaLabel = [
			{ value: days, key: 'days' },
			{ value: hours, key: 'hours' },
			{ value: minutes, key: 'minutes' },
			{ value: seconds, key: 'seconds' },
		]
			.map(({ value, key }) => `${value} ${this.#getLabel(key, value === 1 ? 'singular' : 'plural')}`)
			.join(', ');

		this.setAttribute('aria-label', ariaLabel);
	}

	#parseTarget() {
		if (this.#daily) return this.#nextDailyTarget();

		const date = this.getAttribute('data-date');
		if (!date) return null;

		const time = this.getAttribute('data-time') || '00:00';
		const tz = this.#formatTimezone(this.getAttribute('data-timezone'));

		const parsed = new Date(`${date}T${time}${tz}`);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	}

	#nextDailyTarget() {
		const [hours, minutes] = (this.getAttribute('data-time') || '00:00').split(':').map(Number);
		const target = new Date();
		target.setHours(hours || 0, minutes || 0, 0, 0);

		if (target.getTime() <= Date.now()) target.setDate(target.getDate() + 1);

		return target;
	}

	#formatTimezone(tz) {
		if (!tz || tz === 'Z') return 'Z';
		if (/^[+-]\d{4}$/.test(tz)) return `${tz.slice(0, 3)}:${tz.slice(3)}`;
		if (/^[+-]\d{2}:\d{2}$/.test(tz)) return tz;
		return 'Z';
	}

	#pad(value) {
		return String(value).padStart(2, '0');
	}

	#getLabel(key, form) {
		const part = PARTS.find((item) => item.key === key);
		if (!part) return key;

		const isSingular = form === 'singular';
		const attr = isSingular ? part.singularAttr : part.pluralAttr;
		const fallback = isSingular ? part.fallbackSingular : part.fallbackPlural;

		return this.getAttribute(attr) || fallback;
	}
}

if (!customElements.get('text-countdown-component')) {
	customElements.define('text-countdown-component', TextCountdownComponent);
}
